import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import LandingHeader from './LandingHeader';
import Hero from './Hero';
import Features from './Features';
import Pricing from './Pricing';
import CTA from './CTA';
import LandingFooter from './LandingFooter';
import { Particles } from '../ui/particles';
import SEO from '../SEO';
import { seoConfig } from '../../utils/seoData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faCalendar, faClock, faMobile, faRocket } from '@fortawesome/free-solid-svg-icons';
import { services } from '../../data/services';
import { blogPosts } from '../../data/blogPosts';
import { StackCarousel } from '../ui/stack-carousel';
import { CardCarousel } from '../ui/card-carousel';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import { useLocation } from 'react-router-dom';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore

  const { t } = useTranslation('landing');

  const handleRegisterClick = () => {
    return navigate(`/${lang}/register`);
  }
  useEffect(() => {
    // Handle hash navigation
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
    };

    // Check hash on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const currentLang = lang || 'tr';

  // SEO config based on language
  const seoTitle = currentLang === 'en'
    ? 'Winnomi AI - AI-Powered Social Media Analytics Platform'
    : seoConfig.home.title;
  const seoDescription = currentLang === 'en'
    ? 'Grow your business with AI-powered social media analytics platform. Instagram, Facebook, Twitter, LinkedIn and TikTok analytics in one platform. Real-time data, AI recommendations and comprehensive reporting.'
    : seoConfig.home.description;
  const seoKeywords = currentLang === 'en'
    ? 'social media analytics, instagram analytics, facebook analytics, twitter analytics, linkedin analytics, tiktok analytics, AI analysis, social media management, sosyal medya marketing, social media reporting, social media metrics'
    : seoConfig.home.keywords;

  const pathWithoutLang = location.pathname.replace(/^\/(tr|en)/, '') || '/home';
  const alternateLanguages = {
    'tr': `https://winnomi.com/tr${pathWithoutLang}`,
    'en': `https://winnomi.com/en${pathWithoutLang}`,
    'x-default': `https://winnomi.com/tr${pathWithoutLang}`
  };

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        locale={currentLang === 'en' ? 'en_US' : 'tr_TR'}
        alternateLanguages={alternateLanguages}
        jsonLd={seoConfig.home.jsonLd}
      />
      <div
        className="min-h-screen bg-gray-900 text-white"
        style={{
          minHeight: '100dvh' // Dynamic viewport height for mobile
        }}
      >
        <LandingHeader />

        <main>
          <Hero />
          <div className="px-4 sm:px-6 mb-12 sm:mb-20 pb-4 sm:pb-6 flex justify-center">
            <StackCarousel
              images={[1, 2, 3, 4, 5, 6, 7].map((num) => `/modules/${num}.png`)}
              autoRotate={true}
              autoRotateInterval={4000}
            />
          </div>
          <section id="features" className='bg-gradient-to-b from-gray-900 to-gray-800'>
            <Features />
          </section>
          <section id="pricing" className='bg-gradient-to-b from-gray-800 to-gray-900'>
            <Pricing />
          </section>
          {/* Services Cards Section */}
          <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-900 via-gray-800/90 to-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              {/* Section Header */}
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                  {t('landingPage.services.title')}
                </h2>
                <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto px-4">
                  {t('landingPage.services.subtitle')}
                </p>
              </div>
              <CardCarousel className="mb-8 sm:mb-12">
                {services.slice(0, 3).map((service) => {
                  const categoryLabels: Record<string, string> = {
                    'social-media': t('landingPage.services.categories.socialMedia'),
                    'ai-tools': t('landingPage.services.categories.aiTools'),
                    'analytics': t('landingPage.services.categories.analytics'),
                    'web-analytics': t('landingPage.services.categories.webAnalytics'),
                    'advertising': t('landingPage.services.categories.advertising')
                  };

                  return (
                    <Link
                      key={service.id}
                      to={`/${lang}/hizmetler/${service.slug}`}
                      className="group rounded-xl sm:rounded-2xl overflow-hidden transition-all"
                    >
                      <div className="aspect-video relative overflow-hidden">
                        <img
                          src={service.seo.ogImage || `/services/${service.id}.jpg`}
                          alt={service.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.parentElement;
                            if (fallback) {
                              fallback.className = `aspect-video ${service.bgColor} flex items-center justify-center`;
                              fallback.innerHTML = `<span class="text-4xl sm:text-5xl md:text-6xl">${service.icon}</span>`;
                            }
                          }}
                        />
                      </div>
                      <div className="p-4 sm:p-6">
                        <span className={`inline-block px-2 sm:px-3 py-1 ${service.bgColor} ${service.color} rounded-full text-xs sm:text-sm mb-2 sm:mb-3`}>
                          {categoryLabels[service.category] || service.category}
                        </span>
                        <h3 className="text-lg sm:text-xl font-semibold mb-2 md:group-hover:text-purple-400 transition-colors">
                          {service.title}
                        </h3>
                        <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-3">
                          {service.shortDescription}
                        </p>

                        {/* Key Features */}
                        <div className="mb-3 sm:mb-4">
                          <h4 className="text-xs sm:text-sm font-semibold text-white mb-2">{t('landingPage.services.features')}</h4>
                          <ul className="space-y-1">
                            {service.features.slice(0, 3).map((feature, index) => (
                              <li key={index} className="flex items-start gap-2 text-xs sm:text-sm text-gray-400">
                                <FontAwesomeIcon icon={faCheck} className="text-purple-400 mt-0.5 text-xs flex-shrink-0" />
                                <span className="line-clamp-1">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-white/10">
                          <div className="flex items-center text-purple-400 md:group-hover:gap-2 transition-all">
                            <span className="text-xs sm:text-sm font-medium">{t('landingPage.services.seeDetails')}</span>
                            <FontAwesomeIcon icon={faArrowRight} className="text-xs ml-1" />
                          </div>
                          {service.comingSoon && (
                            <span className="text-xs text-yellow-400 bg-yellow-400/20 px-2 py-1 rounded">
                              {t('landingPage.services.comingSoon')}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </CardCarousel>

              {/* Tümünü İncele Button */}
              <div className="text-center">
                <Link
                  to={`/${lang}/hizmetler`}
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-700 px-20 sm:px-20 md:px-20 py-3 sm:py-4 rounded-xl text-white font-semibold text-sm sm:text-base md:hover:from-purple-600 md:hover:to-purple-800 transition-all duration-300 transform md:hover:scale-105"
                >
                  <span>{t('landingPage.services.viewAll')}</span>
                </Link>
              </div>
            </div>
          </section>

          {/* Bottom CTA */}
          <div className="text-center my-12 sm:my-16 px-4">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-6 sm:p-8 max-w-4xl mx-auto">
              <div className="flex items-center justify-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                <FontAwesomeIcon icon={faMobile} className="text-purple-400 text-xl sm:text-2xl" />
                <FontAwesomeIcon icon={faClock} className="text-blue-400 text-xl sm:text-2xl" />
                <FontAwesomeIcon icon={faRocket} className="text-green-400 text-xl sm:text-2xl" />
              </div>

              <h3
                className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 px-4"
              // style={{
              //   orphans: 4,
              //   widows: 4,
              //   textWrap: 'pretty',
              //   // Safari fallback
              //   WebkitHyphens: 'auto',
              //   MozHyphens: 'auto',
              //   hyphens: 'auto',
              //   wordBreak: 'keep-all',
              //   overflowWrap: 'break-word'
              // }}
              >
                {t('landingPage.bottomCTA.title')}
              </h3>

              <p
                className="text-white/70 mb-4 sm:mb-6 max-w-2xl mx-auto px-4 text-sm sm:text-base"
              // style={{
              //   orphans: 4,
              //   widows: 4,
              //   textWrap: 'pretty',
              //   // Safari fallback
              //   WebkitHyphens: 'auto',
              //   MozHyphens: 'auto',
              //   hyphens: 'auto',
              //   wordBreak: 'keep-all',
              //   overflowWrap: 'break-word'
              // }}
              >
                {t('landingPage.bottomCTA.description')}
              </p>

              <button
                onClick={handleRegisterClick}
                className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-purple-700 px-8 sm:px-12 md:px-20 py-3 sm:py-4 rounded-xl sm:rounded-2xl text-white font-semibold text-base sm:text-lg md:hover:from-purple-600 md:hover:to-purple-800 transition-all duration-300 transform md:hover:scale-105 md:active:scale-95 md:hover:shadow-2xl md:hover:shadow-purple-500/25 touch-manipulation"
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none'
                }}
              >
                {t('landingPage.bottomCTA.button')}
              </button>
            </div>
          </div>

          {/* Blog Cards Section */}
          <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-900 via-gray-900/90 to-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              {/* Section Header */}
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                  {t('landingPage.blog.title')}
                </h2>
                <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto px-4">
                  {t('landingPage.blog.subtitle')}
                </p>
              </div>
              <CardCarousel className="mb-8 sm:mb-12">
                {blogPosts.slice(0, 3).map((post) => {
                  const formatDate = (dateString: string) => {
                    const date = new Date(dateString);
                    const locale = i18n.language === 'en' ? 'en-US' : 'tr-TR';
                    return date.toLocaleDateString(locale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    });
                  };

                  const categoryLabels: Record<string, string> = {
                    'Sosyal Medya Analiği': t('landingPage.blog.categories.Sosyal Medya Analiği'),
                    'AI İçerik Stratejisi': t('landingPage.blog.categories.AI İçerik Stratejisi'),
                    'Instagram Stratejisi': t('landingPage.blog.categories.Instagram Stratejisi'),
                    'Facebook Reklamları': t('landingPage.blog.categories.Facebook Reklamları'),
                    'ROI Analizi': t('landingPage.blog.categories.ROI Analizi'),
                    'Social Media Analytics': t('landingPage.blog.categories.Social Media Analytics'),
                    'AI Content Strategy': t('landingPage.blog.categories.AI Content Strategy'),
                    'Instagram Strategy': t('landingPage.blog.categories.Instagram Strategy'),
                    'Facebook Ads': t('landingPage.blog.categories.Facebook Ads'),
                    'ROI Analysis': t('landingPage.blog.categories.ROI Analysis')
                  };

                  return (
                    <Link
                      key={post.id}
                      to={`/${lang}/blog/${post.slug}`}
                      className="group rounded-xl sm:rounded-2xl overflow-hidden transition-all"
                    >
                      <div className="aspect-video relative overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.parentElement;
                            if (fallback) {
                              fallback.className = 'aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center';
                              fallback.innerHTML = '<span class="text-3xl sm:text-4xl">📝</span>';
                            }
                          }}
                        />
                      </div>
                      <div className="p-4 sm:p-6">
                        <span className="inline-block px-2 sm:px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs sm:text-sm mb-2 sm:mb-3">
                          {categoryLabels[post.category] || post.category}
                        </span>
                        <h3 className="text-lg sm:text-xl font-semibold mb-2 md:group-hover:text-purple-400 transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                            <span className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faCalendar} className="text-xs" />
                              <span className="whitespace-nowrap">{formatDate(post.publishedAt)}</span>
                            </span>
                            <span className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faClock} className="text-xs" />
                              {post.readTime} {t('common.min')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </CardCarousel>

              {/* Tümünü İncele Button */}
              <div className="text-center">
                <Link
                  to={`/${lang}/blog`}
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-700 px-20 sm:px-20 md:px-20 py-3 sm:py-4 rounded-xl text-white font-semibold text-sm sm:text-base md:hover:from-purple-600 md:hover:to-purple-800 transition-all duration-300 transform md:hover:scale-105"
                >
                  <span>{t('landingPage.blog.viewAll')}</span>
                </Link>
              </div>
            </div>
          </section>
          <CTA />
        </main>

        <LandingFooter />
      </div>
    </>
  );
};

export default LandingPage;
